import { adobeAnalyticsScriptsSelector } from '@doda/common/src/redux/features/analytics';
import {
  sc,
  t,
  tl,
  tlReadyExecute,
} from '@doda/common/src/utils/adobeAnalyticsUtil';
import {
  dataLayer,
  getJobIdTypeCase,
  getJobIdScout,
  getJobIdEntryButton,
} from '@doda/common/src/utils/googleTagManagerUtil';
import {
  JobDetailResponse,
  ScoutJobDetailResponse,
} from '@doda/api-client/aspida/@types';
import { AppQueryParams } from '@doda/common/src/constants/queryParams';
import {
  getDeadlineApproachingStatus,
  getEvents,
} from '@doda/common/src/logics/analytics/jobSearchDetail';
import { tabSelector } from '@doda/pc/src/redux/selectors/queryParams';
import type { RecommendTypes } from '@doda/common/src/interfaces/api/recommendPanel';
import { getJobOfferKindIsAgent } from '@doda/common/src/logics/jobSearchDetail/getJobOfferKind';
import { ABTestForManuscriptEPattern } from '@doda/common/src/redux/features/abTest';
import { AppDispatch, RootState } from '@doda/pc/src/redux/store';
import { JOB_DETAIL_TOP4_AS_HIGHLY_RECOMMENDED_AB_TEST_GROUP } from '@doda/common/src/constants/markTop4AsHighlyRecommend';
import { JobDetailTop4AsHighlyRecommendAbTestGroup } from '@doda/common/src/types/markTop4AsHighlyRecommend';
import { Tab } from '../../types/tab';
import { Position, Apply } from '../../types/analytics';
import {
  JOB_SEARCH_DETAIL_ADD_HEADER_MEMBER_REGISTRATION_BUTTON_AB_TEST_GROUP_NO,
  JOB_SEARCH_DETAIL_ADD_HEADER_MEMBER_REGISTRATION_BUTTON_AB_TEST_KEY,
  JOB_SEARCH_DETAIL_MEMBER_REGISTRATION_BUTTON_CLICK,
} from '../../constants';

const createPageName = (tab: Tab) =>
  tab === 'pr' ? '求人詳細（通常）【PR】' : '求人詳細（通常）【募集要項】';

const NORMALIZE_DELAY_PV = 300;

/**
 * JobOfferKindによって適切な文字列を取得する関数
 * @param jobOfferKind
 */
export const getOfferKindText = (jobOfferKind: string): Apply =>
  getJobOfferKindIsAgent(jobOfferKind) ? 'AnkenEntry' : 'Chokusetsu';

/**
 * 求人詳細 PV測定
 * @param jobSearchDetail
 * @param {AppQueryParams} query - クエリパラメータ
 * @param {boolean} isScout - スカウト求人かどうか
 * @param {Tab} tab - タブ状態
 * @param isCasualInterview
 */
export const measureImpression = (
  jobSearchDetail: JobDetailResponse & ScoutJobDetailResponse,
  query: AppQueryParams,
  isScout: boolean,
  tab: Tab,
  isCasualInterview?: boolean
) => {
  const { scVariable, dataLayerVariable } = getAnalyticsVariable(
    jobSearchDetail,
    query,
    isScout,
    isCasualInterview
  );

  const linkTrackVars =
    'pageName,prop56,prop57,prop58,prop59,prop60,prop61,prop62,prop63,events';
  sc({
    channel: '求人詳細',
    pageName: createPageName(tab),
    linkTrackVars,
    ...scVariable,
  });
  t(NORMALIZE_DELAY_PV);

  dataLayer({
    Channel: '求人詳細',
    PageName: createPageName(tab),
    ...dataLayerVariable,
  });
};

/**
 * 求人詳細 レコメンドVisible計測
 * @param {boolean} isAdobeScript - Adobeスクリプトの測定の是非
 * @param {RecommendTypes} recommendType - レコメンドタイプ
 * @param {AppDispatch} dispatch - dispatch
 * @param {string[]} jobIdList - 求人IDリスト
 * @param {Tab} tab  - タブ状態
 */
export const measureVisibleRecommend = (
  isAdobeScript: boolean,
  recommendType: RecommendTypes,
  dispatch: AppDispatch,
  jobIdList: string[],
  tab: Tab
) => {
  // AA測定
  tlReadyExecute(isAdobeScript, dispatch, `recommendVisible:${recommendType}`, {
    linkTrackVars: 'pageName,eVar181,eVar182,eVar183,eVar184',
    channel: '求人詳細',
    pageName: createPageName(tab),
    eVar181: String(jobIdList.length),
    // 表示されるレコメンド求人1～23件迄の求人ID
    eVar182: jobIdList.slice(0, 22)?.join() || '',
    // 表示されるレコメンド求人24～46件迄の求人ID
    eVar183: jobIdList.slice(23, 45)?.join() || '',
    // 表示されるレコメンド求人47～69件迄の求人ID
    eVar184: jobIdList.slice(46, 68)?.join() || '',
  });
};

/**
 * 求人詳細 気になるClick計測
 * @param {boolean} isAdobeScript - Adobeスクリプトの測定の是非
 * @param {AppDispatch} dispatch - dispatch
 * @param {Tab} tab - タブ状態
 * @param jid
 */
export function measureClickFavorite(
  isAdobeScript: boolean,
  dispatch: AppDispatch,
  tab: Tab,
  jid: string
) {
  // AA計測
  tlReadyExecute(isAdobeScript, dispatch, 'kininaruClick_JobSearchDetail', {
    linkTrackVars: 'pageName,events,prop23,eVar23',
    linkTrackEvents: 'event120',
    channel: '求人詳細',
    pageName: createPageName(tab),
    events: 'event120',
    prop23: 'kininaru_Click',
    eVar23: 'kininaru_Click',
  });

  dataLayer({
    JobId: jid,
    event: 'bookmark_click',
    Channel: '求人詳細',
    PageName: createPageName(tab),
  });
}

/**
 * 求人詳細 レコメンド 気になるClick計測
 * @param {boolean} isAdobeScript - Adobeスクリプトの測定の是非
 * @param {RecommendType} recommendType - レコメンドタイプ
 * @param {AppDispatch} dispatch - dispatch
 * @param {string} index - 選択された{index}番目の求人
 * @param {Tab} tab  - タブ情報
 * @param {string} jid - 求人ID
 */
export const measureClickRecommendFavorite = (
  isAdobeScript: boolean,
  recommendType: RecommendTypes,
  dispatch: AppDispatch,
  index: string,
  tab: Tab,
  jid = ''
) => {
  // AA測定 設計書13行目
  tlReadyExecute(isAdobeScript, dispatch, `kininaruClick_JobSearchDetail_rec`, {
    linkTrackVars: 'pageName,events,prop23,prop32,eVar23',
    linkTrackEvents: 'event120',
    events: 'event120',
    products: `;${jid}`,
    prop23: 'kininaru_Click',
    prop32: jid,
    channel: '求人詳細',
    pageName: createPageName(tab),
    eVar23: 'kininaru_Click',
  });
  // GTM測定 設計書28行目
  dataLayer({
    click_Label: 'event70',
    click_Action: `Rekome_pc${index}k`,
    click_Category: `${recommendType}_pc${index}_kentou`,
  });
  // GTM測定 設計書29行目
  dataLayer({
    JobId: jid,
    event: 'bookmark_click',
    Channel: '求人詳細',
    PageName: createPageName(tab),
  });
};

/**
 * 求人詳細 レコメンド  求人カードクリック
 * @param {boolean} isAdobeScript - Adobeスクリプトの測定の是非
 * @param {RecommendTypes} recommendType - レコメンドタイプ
 * @param {string} effectName - EffectName
 * @param {AppDispatch} dispatch - dispatch
 * @param {string} index - 選択された{index}番目の求人
 * @param {Tab} tab  - タブ情報
 */
export const measureClickRecommendJobCardAdobe = (
  isAdobeScript: boolean,
  recommendType: RecommendTypes,
  effectName: string,
  dispatch: AppDispatch,
  index: string,
  tab: Tab
) => {
  // AA測定
  tlReadyExecute(isAdobeScript, dispatch, 'siteRecommendTracking', {
    linkTrackVars: 'pageName,prop23,eVar23,eVar103,eVar104,eVar105,eVar187',
    channel: '求人詳細',
    pageName: createPageName(tab),
    prop23: `Rekome_pc${index}i`,
    eVar23: `Rekome_pc${index}i`,
    eVar103: recommendType,
    eVar104: effectName,
    eVar105: '求人詳細',
    eVar187: createPageName(tab),
  });
};

/**
 * 求人詳細 ポジション気になる一覧遷移 Click計測
 * @param {boolean} isAdobeScript - Adobeスクリプトの測定の是非
 * @param {AppDispatch} dispatch - dispatch
 * @param {Position} position - 押されたボタンの位置
 * @param {Tab} tab - タブ情報
 * @param {string} jid - 求人ID
 */
export function measureClickBookmarkSavedPosition(
  isAdobeScript: boolean,
  dispatch: AppDispatch,
  position: Position,
  tab: Tab,
  jid = ''
) {
  // AA計測
  tlReadyExecute(
    isAdobeScript,
    dispatch,
    `KyujinShousai_ActionButton_${position === 'Top' ? 'Top' : 'Buttom'}Kentouchu`,
    {
      linkTrackVars: 'pageName,events,prop23,eVar23',
      linkTrackEvents: 'event81',
      events: 'event81',
      channel: '求人詳細',
      pageName: createPageName(tab),
      products: `;${jid}`,
      prop23: `KyujinShousai_${position === 'Top' ? 'Top' : 'Buttom'}Kentouchu`,
      prop32: jid,
      eVar23: `KyujinShousai_${position === 'Top' ? 'Top' : 'Buttom'}Kentouchu`,
    }
  );

  // GTM測定
  dataLayer({
    JobId: jid,
    event: 'setEffect',
    click_Label: 'event81',
    click_Action: `KyujinShousai_${position === 'Top' ? 'Top' : 'Buttom'}Kentouchu`,
    click_Category: `KyujinShousai_ActionButton_${position === 'Top' ? 'Top' : 'Buttom'}Kentouchu`,
    Channel: '求人詳細',
    PageName: createPageName(tab),
  });
}

/**
 * 気になる計測Thunk
 * @param root0
 */
export function measureBookmark({
  initialTab,
  jid,
}: {
  initialTab: Tab;
  jid: string;
}) {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const isAdobeLoaded = adobeAnalyticsScriptsSelector(getState());
    const tab = tabSelector(getState()) || initialTab;

    measureClickFavorite(isAdobeLoaded, dispatch, tab, jid);
  };
}

/**
 * 気になる一覧遷移計測Thunk
 * @param root0
 */
export function measureBookmarkSaved({
  position,
  initialTab,
  jid,
}: {
  position: Position;
  initialTab: Tab;
  jid: string;
}) {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const isAdobeLoaded = adobeAnalyticsScriptsSelector(getState());
    const tab = tabSelector(getState()) || initialTab;

    measureClickBookmarkSavedPosition(
      isAdobeLoaded,
      dispatch,
      position,
      tab,
      jid
    );
  };
}

/**
 * 求人詳細 ポジション応募ボタン計測
 * @param {boolean} isAdobeScript - Adobeスクリプトの測定の是非
 * @param {AppDispatch} dispatch - dispatch
 * @param {Position} position - 押されたボタンの位置
 * @param {Apply} apply - 応募形態
 * @param {Tab} tab - タブ情報
 * @param {string} jid - 求人ID
 */
export const measureClickApplyPosition = (
  isAdobeScript: boolean,
  dispatch: AppDispatch,
  position: Position,
  apply: Apply,
  tab: Tab,
  jid = ''
) => {
  // AA測定
  tlReadyExecute(
    isAdobeScript,
    dispatch,
    `KyujinShousai_ActionButton_${position === 'Top' ? 'Top' : 'Buttom'}${apply}`,
    {
      linkTrackVars: 'pageName,events,prop23,eVar23',
      linkTrackEvents: 'event81',
      events: 'event81',
      prop23: `KyujinShousai_${position === 'Top' ? 'Top' : 'Buttom'}${apply}`,
      channel: '求人詳細',
      pageName: createPageName(tab),
      eVar23: `KyujinShousai_${position === 'Top' ? 'Top' : 'Buttom'}${apply}`,
    }
  );

  // GTM測定
  dataLayer({
    JobId: jid,
    click_Label: 'event81',
    click_Action: `KyujinShousai_${position === 'Top' ? 'Top' : 'Buttom'}${apply}`,
    click_Category: `KyujinShousai_ActionButton_${position === 'Top' ? 'Top' : 'Buttom'}${apply}`,
    event: 'setEffect',
    Channel: '求人詳細',
    PageName: createPageName(tab),
  });
};

/**
 * 応募計測Thunk
 * @param root0
 */
export function measureApply({
  position,
  apply,
  initialTab,
  jid,
}: {
  position: Position;
  apply: Apply;
  initialTab: Tab;
  jid: string;
}) {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const isAdobeLoaded = adobeAnalyticsScriptsSelector(getState());
    const tab = tabSelector(getState()) || initialTab;

    measureClickApplyPosition(
      isAdobeLoaded,
      dispatch,
      position,
      apply,
      tab,
      jid
    );
  };
}

/**
 * 求人詳細 募集要項タブ 給与Visible
 * @param {boolean} isAdobeScript - Adobeスクリプトの測定の是非
 * @param {AppDispatch} dispatch - dispatch
 */
export const measureVisibleSalary = (
  isAdobeScript: boolean,
  dispatch: AppDispatch
) => {
  // AA測定
  tlReadyExecute(isAdobeScript, dispatch, `salary_visible`, {});
};

/**
 * 求人詳細 募集要項タブ 50%スクロール
 * @param {boolean} isAdobeScript - Adobeスクリプトの測定の是非
 * @param {AppDispatch} dispatch - dispatch
 */
export const measureScroll50 = (
  isAdobeScript: boolean,
  dispatch: AppDispatch
) => {
  // AA測定
  tlReadyExecute(isAdobeScript, dispatch, `view_50%scroll`, {
    linkTrackVars: 'pageName,events',
    linkTrackEvents: 'event216',
    events: 'event216',
    channel: '求人詳細',
    pageName: '求人詳細（通常）【募集要項】',
  });
};

/**
 * 求人詳細 応募促進モーダル PV計測
 *  - 求人情報
 * @param jobSearchDetail
 * @param {AppQueryParams} query - クエリパラメータ
 * @param {boolean} isScout - スカウト求人かどうか
 * @param {Apply} apply - 応募形態
 * @param isCasualInterview
 */
export const measureImpressionApplyModal = (
  jobSearchDetail: JobDetailResponse & ScoutJobDetailResponse,
  query: AppQueryParams,
  isScout: boolean,
  apply: Apply,
  isCasualInterview?: boolean
) => {
  const { propsVariable } = getAnalyticsVariable(
    jobSearchDetail,
    query,
    isScout,
    isCasualInterview
  );
  // AA測定
  const linkTrackVars =
    'pageName,prop56,prop57,prop58,prop59,prop60,prop61,prop62,prop63,events';

  // PV計測より前に発火しないように発火を遅らせる
  // FIXME: setTimeoutではなくロジックで制御できるようにしたい
  setTimeout(() => {
    sc({
      channel: '求人詳細（モーダル）',
      pageName: `求人詳細（モーダル）_${apply === 'AnkenEntry' ? '紹介' : '直接'}応募`,
      linkTrackVars,
      ...propsVariable,
    });
    t();
  }, NORMALIZE_DELAY_PV + 500);
};

/**
 * 求人詳細 応募促進モーダル応募ボタン計測
 * @param {boolean} isAdobeScript - Adobeスクリプトの測定の是非
 * @param {AppDispatch} dispatch - dispatch
 * @param {Apply} apply - 応募形態
 * @param {string} jid - 求人ID
 */
export const measureClickApplyModalApply = (
  isAdobeScript: boolean,
  dispatch: AppDispatch,
  apply: Apply,
  jid = ''
) => {
  // AA測定
  tlReadyExecute(
    isAdobeScript,
    dispatch,
    `PC_kyujinshousai_modal_${apply === 'AnkenEntry' ? 'shoukai' : 'Chokusetsu'}_oubo`,
    {
      linkTrackVars: 'products,pageName,events,prop23,prop32,eVar23',
      products: `;${jid}`,
      channel: '求人詳細（モーダル）',
      pageName: `求人詳細（モーダル）_${apply === 'AnkenEntry' ? '紹介' : '直接'}応募`,
      prop23: `PC_kyujinshousai_modal_${apply === 'AnkenEntry' ? 'shoukai' : 'Chokusetsu'}_oubo`,
      prop32: jid,
      eVar23: `PC_kyujinshousai_modal_${apply === 'AnkenEntry' ? 'shoukai' : 'Chokusetsu'}_oubo`,
    }
  );
};

/**
 * 求人詳細 応募促進モーダル気になるボタン計測
 * @param {boolean} isAdobeScript - Adobeスクリプトの測定の是非
 * @param {AppDispatch} dispatch - dispatch
 * @param {Apply} apply - 応募形態
 * @param {string} jid - 求人ID
 */
export const measureClickApplyModalFavorite = (
  isAdobeScript: boolean,
  dispatch: AppDispatch,
  apply: Apply,
  jid = ''
) => {
  // AA測定
  tlReadyExecute(
    isAdobeScript,
    dispatch,
    `PC_kyujinshousai_modal_${apply === 'AnkenEntry' ? 'shoukai' : 'Chokusetsu'}_kininaru`,
    {
      linkTrackVars: 'products,pageName,events,prop23,prop32,eVar23',
      linkTrackEvents: 'event120',
      events: 'event120',
      products: `;${jid}`,
      channel: '求人詳細（モーダル）',
      pageName: `求人詳細（モーダル）_${apply === 'AnkenEntry' ? '紹介' : '直接'}応募`,
      prop23: `PC_kyujinshousai_modal_${apply === 'AnkenEntry' ? 'shoukai' : 'Chokusetsu'}_kininaru`,
      prop32: jid,
      eVar23: `PC_kyujinshousai_modal_${apply === 'AnkenEntry' ? 'shoukai' : 'Chokusetsu'}_kininaru`,
    }
  );
};

/**
 * 求人詳細 企業バナーvisible計測
 * @param {boolean} isVisible - 企業バナー表示フラグ
 * @param {string} jid - 求人ID
 */
export const measureVisibleCompanyPageLinkBanner = (
  isVisible: boolean,
  jid: string
) => {
  const linkName = isVisible
    ? 'KGM_genkou_Link_imp'
    : 'KGM_genkou_Link_none_imp';
  // PV計測より前に発火しないように発火を遅らせる
  // FIXME: setTimeoutではなくロジックで制御できるようにしたい
  setTimeout(() => {
    tl(linkName, {
      linkTrackVars: 'prop32',
      products: `;${jid}`,
      prop32: jid,
    });
  }, NORMALIZE_DELAY_PV + 500);
};

/**
 * 求人詳細 企業バナークリック計測
 * @param {boolean} isAdobeScript - Adobeスクリプトの測定の是非
 * @param {AppDispatch} dispatch - dispatch
 * @param {string} jid - 求人ID
 * @param {React.MouseEvent<HTMLAnchorElement>} event - event
 * @param {string} url - 移動先リンク
 * @param {string | undefined} message_id - メッセージID
 * @param {string | undefined} scoutlist_id - リストID
 * @param {string | undefined} osscfl - スカウト区分
 * @param {string | undefined} wlstid - スカウトリストID
 * @param {boolean} hasArticleContent - 記事コンテンツ取得結果フラグ
 */
export const measureClickCompanyPageLinkBanner = (
  isAdobeScript: boolean,
  dispatch: AppDispatch,
  jid: string,
  event: React.MouseEvent<HTMLAnchorElement>,
  url: string,
  message_id: string | undefined,
  scoutlist_id: string | undefined,
  osscfl: string | undefined,
  wlstid: string | undefined,
  hasArticleContent: boolean
) => {
  if (!url) {
    return;
  }
  event.preventDefault();
  const OR_INFORMATION_MAIL_COMMIT = `12`;
  const hasMessageAndScout = message_id && scoutlist_id;
  const hasOsscflAndWlstid =
    osscfl && osscfl === OR_INFORMATION_MAIL_COMMIT && wlstid;
  let companyPageUrl = `${url}?jid=${jid}`;
  if (hasMessageAndScout) {
    companyPageUrl += `&message_id=${message_id}&scoutlist_id=${scoutlist_id}`;
  } else if (hasOsscflAndWlstid) {
    companyPageUrl += `&osscfl=${osscfl}&wlstid=${wlstid}`;
  }
  if ((hasMessageAndScout || hasOsscflAndWlstid) && hasArticleContent) {
    companyPageUrl += `&hasPremiumOffer=true`;
  }
  tlReadyExecute(isAdobeScript, dispatch, 'KGM_genkou_Link_Click_footer', {
    linkTrackVars: 'prop32',
    products: `;${jid}`,
    prop32: jid,
  });
  window.open(companyPageUrl, '_blank');
};

/**
 * 求人詳細 AA/GA変数を取得する
 * @param {JobDetailResponse & ScoutJobDetailResponse} jobSearchDetail
 * @param {AppQueryParams} query
 * @param {boolean} isScout
 * @param isCasualInterview
 */
const getAnalyticsVariable = (
  jobSearchDetail: JobDetailResponse & ScoutJobDetailResponse,
  query: AppQueryParams,
  isScout: boolean,
  isCasualInterview?: boolean
) => {
  const { jid, wcnsId, docType, jobOfferKind, movieId } =
    jobSearchDetail.job ?? {};
  const {
    industryLName,
    industrySName,
    searchJobTypeLName,
    searchJobTypeMName,
    searchJobTypeSName,
    searchWorkLocations,
  } = jobSearchDetail.job?.analysis ?? {};

  const jobIdTypeCase = getJobIdTypeCase(docType ?? '', jobOfferKind ?? '');
  const jobIdScout = getJobIdScout(
    docType ?? '',
    jobOfferKind ?? '',
    isCasualInterview
  );
  const jobIdEntryButton = getJobIdEntryButton(jobOfferKind ?? '');

  const isRecommend = Boolean(query.recommendID);
  const isFavorited = jobSearchDetail?.isBookmarkSaved ?? false;
  const isApproachingTheDeadline =
    getDeadlineApproachingStatus(jobSearchDetail);
  const isRead = jobSearchDetail?.isRead ?? false;
  const events = getEvents(
    isScout,
    isRecommend,
    query.from,
    isFavorited,
    isApproachingTheDeadline,
    movieId,
    isRead
  );

  // Props変数
  const propsVariable = {
    products: `;${jid}`, // 求人ページID
    prop32: jid, // 求人ページID
    prop56: industryLName, // 【求人属性】業種大分類
    prop57: industrySName, // 【求人属性】業種小分類
    prop58: searchJobTypeLName, // 【求人属性】職種大分類
    prop59: searchJobTypeMName, // 【求人属性】職種中分類
    prop60: searchJobTypeSName, // 【求人属性】職種小分類
    prop61: searchWorkLocations?.join('/'), // 【求人属性】エリア
    prop62: `${docType}:${wcnsId}`, // 【求人属性】原稿種別
    prop63: `${jobIdTypeCase}:${jobIdScout}:${jobIdEntryButton}`, // 【求人属性】案件種別
  };

  // AA変数
  const scVariable = {
    ...propsVariable,
    linkTrackEvents: events,
    events,
  };

  // GA変数
  const dataLayerVariable = {
    JobIdIndLargeClass: industryLName,
    JobIdIndSmallClass: industrySName,
    JobIdCatLargeClass: searchJobTypeLName,
    JobIdCatMiddleClass: searchJobTypeMName,
    JobIdCatSmallClass: searchJobTypeSName,
    JobIdArea: searchWorkLocations?.join('/'),
    JobIdDocType: docType,
    JobIdDocSize: wcnsId,
    JobId: jid,
    JobIdTypeCase: jobIdTypeCase,
    JobIdScout: jobIdScout,
    JobIdEntryButton: jobIdEntryButton,
  };

  return { propsVariable, scVariable, dataLayerVariable };
};

/**
 * 求人詳細 E原稿動画ABテスト 求人詳細募集要項タブPV計測
 * @param {number} abTestForManuscriptEGroupNo - E原稿動画ABテストグループNo.
 */
export const measureAbTestForManuscriptE = (
  abTestForManuscriptEGroupNo: number
) => {
  let costomLink: string | undefined;
  switch (abTestForManuscriptEGroupNo) {
    case ABTestForManuscriptEPattern.A:
      costomLink = 'test_e_detail_A';
      break;
    case ABTestForManuscriptEPattern.B:
      costomLink = 'test_e_detail_B';
      break;
    case ABTestForManuscriptEPattern.Original:
      costomLink = 'test_e_detail_original';
      break;
    default:
      break;
  }

  if (costomLink) {
    // PV計測より前に発火しないように発火を遅らせる
    setTimeout(() => {
      tl(costomLink, {});
    }, NORMALIZE_DELAY_PV + 500);
  }
};

/**
 * 求人詳細 PV計測 写真タブ (GTM)
 * @param {JobDetailResponse & ScoutJobDetailResponse} jobSearchDetail
 * @param {AppQueryParams} query
 * @param {boolean} isScout
 * @param isCasualInterview
 */
export const measureImpressionPublicationPhotoGtm = (
  jobSearchDetail: JobDetailResponse & ScoutJobDetailResponse,
  query: AppQueryParams,
  isScout: boolean,
  isCasualInterview?: boolean
) => {
  const { scVariable, dataLayerVariable } = getAnalyticsVariable(
    jobSearchDetail,
    query,
    isScout,
    isCasualInterview
  );

  const linkTrackVars =
    'prop32,prop56,prop57,prop58,prop59,prop60,prop61,prop62,prop63,eVar56,eVar119,products';
  // AA計測
  sc({
    channel: 'PC_求人詳細',
    pageName: 'PC_求人詳細（通常）【写真】',
    linkTrackVars,
    ...scVariable,
  });
  t();
  dataLayer({
    Channel: 'PC_求人詳細',
    PageName: 'PC_求人詳細（通常）【写真】',
    ...dataLayerVariable,
  });
};

/**
 * PC)求人詳細 ヘッダー内登録ボタン表示ABテスト計測
 * @param {string} abTestNo - abTestNo
 */
export const measureVisibleHeaderRegistrationButton = (abTestNo: string) => {
  // AA測定
  if (!abTestNo || abTestNo === '0') return;
  const linkName =
    JOB_SEARCH_DETAIL_ADD_HEADER_MEMBER_REGISTRATION_BUTTON_AB_TEST_KEY +
    (abTestNo ===
    JOB_SEARCH_DETAIL_ADD_HEADER_MEMBER_REGISTRATION_BUTTON_AB_TEST_GROUP_NO
      ? '_test'
      : '_original');
  // pvより後に発火させる
  setTimeout(() => {
    tl(linkName, {});
  }, 1000);
};

/**
 * PC)求人詳細 ヘッダー内登録ボタンクリック計測
 */
export const measureHeaderRegistrationButtonClick = () => {
  tl(JOB_SEARCH_DETAIL_MEMBER_REGISTRATION_BUTTON_CLICK, {});
};

/**
 * Measure AB test allocation for DODASITE-23658
 * Fired once when AB test pattern is determined and at least one target panel is displayed
 *
 * @param pattern - AB test pattern (ORIGINAL, TEST1, TEST2, FAILED)
 * @param hasTargetPanels - Whether target panels are displayed
 */
export const measureJobDetailRecommendABTestAllocation = (
  pattern: JobDetailTop4AsHighlyRecommendAbTestGroup,
  hasTargetPanels: boolean
): void => {
  // Skip if no target panels displayed
  if (!hasTargetPanels) return;

  // Skip if allocation failed
  if (pattern === JOB_DETAIL_TOP4_AS_HIGHLY_RECOMMENDED_AB_TEST_GROUP.FAILED)
    return;

  // Map pattern to event name
  const eventNameMap: Record<string, string> = {
    [JOB_DETAIL_TOP4_AS_HIGHLY_RECOMMENDED_AB_TEST_GROUP.ORIGINAL]:
      'custom_ab_dodasite_23658_original',
    [JOB_DETAIL_TOP4_AS_HIGHLY_RECOMMENDED_AB_TEST_GROUP.TEST1]:
      'custom_ab_dodasite_23658_test1',
    [JOB_DETAIL_TOP4_AS_HIGHLY_RECOMMENDED_AB_TEST_GROUP.TEST2]:
      'custom_ab_dodasite_23658_test2',
  };

  const eventName = eventNameMap[pattern];
  if (!eventName) return;

  // Fire tracking event
  tl(eventName, {});
};

/**
 * Measure high-score job click for DODASITE-23658
 * Called when user clicks on a job card where isHighlyRecommended=true
 *
 * @param recommendType - Type of recommendation panel
 */
export const measureHighScoreJobClick = (recommendType: string): void => {
  if (!recommendType) return;
  const eventName = `custom_click_cassette_high_score_${recommendType}`;

  // Fire tracking event
  tl(eventName, {});
};

/**
 * DODASITE-29189 求人詳細_T原稿3点要約表示 計測に付ける求人属性（prop32/62/63）
 * @param {JobDetailResponse & ScoutJobDetailResponse} jobSearchDetail - 求人詳細情報
 * @param {AppQueryParams} query - クエリパラメータ
 * @param {boolean} isScout - スカウト求人かどうか
 * @param {boolean} isCasualInterview - カジュアル面談か
 * @returns tlのオプション
 */
const getTManuscriptSummaryLinkTrackOption = (
  jobSearchDetail: JobDetailResponse & ScoutJobDetailResponse,
  query: AppQueryParams,
  isScout: boolean,
  isCasualInterview?: boolean
) => {
  const { propsVariable } = getAnalyticsVariable(
    jobSearchDetail,
    query,
    isScout,
    isCasualInterview
  );
  return {
    linkTrackVars: 'prop32,prop62,prop63',
    prop32: propsVariable.prop32,
    prop62: propsVariable.prop62,
    prop63: propsVariable.prop63,
  };
};

/**
 * DODASITE-29189 求人詳細_T原稿3点要約表示 振り分け計測
 * @param {'ORIGINAL' | 'TEST'} groupNo - ABテストグループ
 * @param {JobDetailResponse & ScoutJobDetailResponse} jobSearchDetail - 求人詳細情報
 * @param {AppQueryParams} query - クエリパラメータ
 * @param {boolean} isScout - スカウト求人かどうか
 * @param {boolean} isCasualInterview - カジュアル面談か
 */
export const measureAbTestTManuscriptSummary = (
  groupNo: 'ORIGINAL' | 'TEST',
  jobSearchDetail: JobDetailResponse & ScoutJobDetailResponse,
  query: AppQueryParams,
  isScout: boolean,
  isCasualInterview?: boolean
) => {
  const linkNameMap = {
    ORIGINAL: 'custom_ab_dodaapp_38870_original',
    TEST: 'custom_ab_dodaapp_38870_test',
  };

  tl(
    linkNameMap[groupNo],
    getTManuscriptSummaryLinkTrackOption(
      jobSearchDetail,
      query,
      isScout,
      isCasualInterview
    )
  );
};

/**
 * DODASITE-29189 求人詳細_T原稿3点要約表示 3点要約エリアのimp計測
 * @param {JobDetailResponse & ScoutJobDetailResponse} jobSearchDetail - 求人詳細情報
 * @param {AppQueryParams} query - クエリパラメータ
 * @param {boolean} isScout - スカウト求人かどうか
 * @param {boolean} isCasualInterview - カジュアル面談か
 */
export const measureImpressionTManuscriptSummary = (
  jobSearchDetail: JobDetailResponse & ScoutJobDetailResponse,
  query: AppQueryParams,
  isScout: boolean,
  isCasualInterview?: boolean
) => {
  tl(
    'custom_ab_imp_JobSearchDetail_summary',
    getTManuscriptSummaryLinkTrackOption(
      jobSearchDetail,
      query,
      isScout,
      isCasualInterview
    )
  );
};
